import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { beautifyTheLowHighObject, isNotEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { useSelector } from 'react-redux';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { PlusCircleFilled } from '@ant-design/icons';
import { getCityPermission } from '@/common/api/siteselectionmap';
import { getCompanyCityPermission, getYouthMapDefaultConfig, getYouthMapModelClusterFlowCompare } from '@/common/api/youth';
import { getXTCPermissionCity } from '@/common/api/xtc/youth';
import { getYouthPageList } from '@/common/api/networkplan';
import { useTenantCheckInfo } from '@/common/hook/useTenantCheckInfo';
import { Checkbox, Input, Popover, Spin, Typography } from 'antd';
import { kaXTC } from '@/ka/xtc/config';
import cs from 'classnames';
import styles from './DailyFlowTrend.module.less';
import dayjs from 'dayjs';
import V2Empty from '@/common/components/Data/V2Empty';
import V2LineChart from '@/common/components/Charts/V2LineChart';
import V2Message from '@/common/components/Others/V2Hint/V2Message';

interface DailyFlowTrendProps {
  /** 当前商圈详情 */
  detail: any;
  /** 模型配置 ID */
  modelConfigId: number;
  /** 日客流趋势数据 */
  dailyFlowChartData: any;
  /** 节假日日期映射 */
  holidayDateMap: Record<string, string>;
  /** 日期筛选器当前值 */
  range: string[];
}

interface CompareOption {
  label: string;
  value: number;
}

/** 日客流趋势及商圈对比模块。 */
const DailyFlowTrend: FC<DailyFlowTrendProps> = ({
  detail,
  modelConfigId,
  dailyFlowChartData,
  holidayDateMap,
  range,
}) => {
  const [selectedIds, setSelectedIds] = useState<number[]>(detail?.id ? [detail.id] : []);
  const [options, setOptions] = useState<CompareOption[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [compareData, setCompareData] = useState<any>(null);
  const [keyword, setKeyword] = useState<string>('');
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [pendingPopoverOpen, setPendingPopoverOpen] = useState<boolean>(false);
  const [cityIds, setCityIds] = useState<number[] | undefined>();
  const [hasAllCityPermission, setHasAllCityPermission] = useState<boolean>(false);
  const cityPermissionRequestRef = useRef<Promise<number[]> | null>(null);
  const hasAllCityPermissionRef = useRef<boolean>(false);
  const cityForAMap = useSelector((state: any) => state.common.cityForAMap);
  const { tenantCheckInfo } = useTenantCheckInfo();
  const isXTC = kaXTC(tenantCheckInfo);

  const methods = useMethods({
    getCurrentOption() {
      // 构造当前商圈的对比选项，标记在列表中且不可被取消选中
      return {
        label: detail?.areaName || detail?.centerName || detail?.id,
        value: detail?.id,
      };
    },
    getCityIds() {
      if (cityIds) {
        return Promise.resolve(cityIds);
      }
      if (cityPermissionRequestRef.current) {
        return cityPermissionRequestRef.current;
      }
      // 先获取地图模式，再按模式和租户类型获取城市权限，确保商圈列表请求使用正确的城市范围。
      const request = getYouthMapDefaultConfig({ modelConfigId })
        .then((config) => {
          const permissionRequest = isXTC
            ? getXTCPermissionCity()
            : config?.youthMapMode === 1 ? getCityPermission() : getCompanyCityPermission();
          return permissionRequest;
        })
        .then((permission) => {
          // 拥有全部城市权限时使用地图维护的全量城市 ID，否则使用权限接口返回的 ids。
          const targetCityIds = permission?.hasAllPermission
            ? cityForAMap.map((item) => item.id)
            : (Array.isArray(permission?.ids) ? permission.ids : []);
          hasAllCityPermissionRef.current = !!permission?.hasAllPermission;
          setHasAllCityPermission(!!permission?.hasAllPermission);
          setCityIds(targetCityIds);
          return targetCityIds;
        })
        .finally(() => {
          cityPermissionRequestRef.current = null;
        });
      cityPermissionRequestRef.current = request;
      return request;
    },
    getAreaList(name = '', targetCityIds = cityIds) {
      // 拉取同城商圈列表作为可对比选项；搜索结果需与已选中项、历史选项合并去重，避免已选项丢失
      // cityIds 为必传参数；用户没有任何城市权限时不发送空数组，避免接口被误解析为全量城市。
      if (!detail?.id || !targetCityIds?.length) {
        return;
      }
      setSearchLoading(true);
      getYouthPageList({
        // provinceIds: detail?.provinceId ? [detail.provinceId] : undefined,
        cityIds: targetCityIds,
        modelConfigId,
        // onlyLatLng: true,
        centerName: name,
        // 写死，只要商场数据
        secondLevelCategories: ['商业体'],
        page: 1,
        // showPolygon: false,
        size: 50,
      }).then((res) => {
        const currentOption = methods.getCurrentOption();
        const selectedOptions = selectedIds.map((id) => {
          if (id === currentOption.value) {
            return currentOption;
          }
          const selectedOption = options.find((item) => item.value === id);
          return selectedOption || { label: id, value: id };
        });
        const resultOptions = Array.isArray(res) ? res.map((item) => ({
          label: item.areaName || item.centerName || item.id,
          value: item.id,
        })) : [];
        // 合并已选项 + 搜索结果，并按 value 去重
        const mergedOptions = [
          ...selectedOptions,
          ...resultOptions.filter((item) => !selectedIds.includes(item.value)),
        ].filter((item, index, list) => list.findIndex((option) => option.value === item.value) === index);
        setOptions(mergedOptions);
      }).finally(() => {
        setSearchLoading(false);
      });
    },
    fetchCompareData() {
      // 选中商圈数 > 1 且有月份范围时才发起对比接口，否则清空对比数据(回退单商圈展示)
      if (selectedIds.length <= 1 || !range?.length) {
        setCompareData(null);
        return;
      }
      getYouthMapModelClusterFlowCompare({
        ids: selectedIds,
        startMonth: range[0],
        endMonth: range[1],
      }).then((res) => {
        setCompareData(res || null);
      });
    },
    onSearchChange(event) {
      const value = event.target.value;
      setKeyword(value);
      methods.getAreaList(value);
    },
    onCompareChange(value: CheckboxValueType[]) {
      // 勾选对比商圈：始终保留当前商圈 id；限定可对比商圈数不超过 3 个(含当前商圈)
      const currentId = detail?.id;
      if (!currentId) {
        return;
      }
      // Checkbox.Group 回调值为 CheckboxValueType[]，需转回 number[] 以匹配 selectedIds
      const nextIds = Array.from(new Set([currentId, ...value.map((item) => Number(item))]));
      if (nextIds.length > 3) {
        V2Message.warning(`最多支持3个商圈`);
        return;
      }
      setSelectedIds(nextIds);
    },
    onPopoverChange(open) {
      // 城市权限是异步获取的，必须拿到 cityIds 后才真正打开弹层并请求商圈列表。
      if (!open) {
        setPopoverOpen(false);
        setPendingPopoverOpen(false);
        return;
      }
      if (popoverOpen) return;
      methods.getCityIds().then((targetCityIds) => {
        // 全量权限依赖 Redux 中的行政区数据；数据尚未加载时延后到 cityForAMap 就绪后再打开。
        if (hasAllCityPermissionRef.current && !targetCityIds.length) {
          setPendingPopoverOpen(true);
          return;
        }
        setPopoverOpen(true);
        if (targetCityIds.length) {
          methods.getAreaList(keyword, targetCityIds);
        }
      });
    },
    formatTooltipValue(value) {
      // 客流数值 tooltip 美化：按数值大小自动选择「人次/万人次/亿人次」单位
      const target = beautifyTheLowHighObject(1, value, 1, { low: '人次', high: '万人次', top: '亿人次' });
      return isNotEmpty(value) ? `${target.val}${target.unit}` : '-';
    },
    formatTooltipItem(item) {
      const value = methods.formatTooltipValue(item?.value);
      return `<div>${item?.marker || ''}${value}</div>`;
    },
    dailyTooltipFormatter(params) {
      // 每日客流 tooltip：x 轴(日期)为标题，展示各系列客流值，并追加当天节假日标签
      const firstItem = Array.isArray(params) ? params[0] : params;
      const label = firstItem?.axisValueLabel || '-';
      const dataList = Array.isArray(params) ? params : [params];
      let dataText = '';
      for (let index = 0; index < dataList.length; index += 1) {
        dataText += methods.formatTooltipItem(dataList[index]);
      }
      const holiday = labelHolidayMap[label] ? `<span style='display:inline-block;margin-top:4px;background:#FFC136;color:#ffffff;padding:1px 6px;border-radius:9px;font-size:12px;line-height:18px;'>${labelHolidayMap[label]}</span>` : '';
      return `<div style='margin-bottom:4px;'>${label}</div>${dataText}${holiday}`;
    },
    renderCompareOption(item: CompareOption) {
      return (
        <Checkbox
          key={item.value}
          value={item.value}
          disabled={item.value === detail?.id}
        >
          <Typography.Text ellipsis={{ tooltip: item.label }}>
            {item.label}
          </Typography.Text>
        </Checkbox>
      );
    },
    renderCompareContent() {
      return (
        <div className={styles.addListContent}>
          <Input.Search
            className={styles.listInputSearch}
            placeholder='搜索关键词'
            value={keyword}
            onChange={methods.onSearchChange}
          />
          <div className={styles.listContent}>
            <Spin spinning={searchLoading}>
              <Checkbox.Group
                value={selectedIds}
                onChange={methods.onCompareChange}
              >
                {options.map(methods.renderCompareOption)}
              </Checkbox.Group>
              {!searchLoading && !options.length && <div className={styles.emptyWrap}><V2Empty /></div>}
            </Spin>
          </div>
        </div>
      );
    },
  });

  useEffect(() => {
    // 全量权限依赖 cityForAMap，若行政区数据晚于权限接口返回，则在其到达后补齐城市 ID。
    if (hasAllCityPermission && cityForAMap.length) {
      const allCityIds = cityForAMap.map((item) => item.id);
      setCityIds(allCityIds);
      // 用户已点击对比按钮时，城市数据到位后才打开弹层并拉取列表。
      if (pendingPopoverOpen) {
        setPendingPopoverOpen(false);
        setPopoverOpen(true);
        methods.getAreaList(keyword, allCityIds);
      }
    }
  }, [cityForAMap, hasAllCityPermission, pendingPopoverOpen]);

  useEffect(() => {
    // 切换商圈或模型后清理旧权限城市，避免新详情复用旧城市范围。
    cityPermissionRequestRef.current = null;
    setCityIds(undefined);
    setHasAllCityPermission(false);
    hasAllCityPermissionRef.current = false;
    setPopoverOpen(false);
    setPendingPopoverOpen(false);
  }, [detail?.id, modelConfigId]);

  useEffect(() => {
    // detail 变化时重置对比状态：默认仅选中当前商圈，清空搜索关键词与选项
    const currentId = detail?.id;
    setSelectedIds(currentId ? [currentId] : []);
    setCompareData(null);
    setKeyword('');
    setOptions(currentId ? [methods.getCurrentOption()] : []);
  }, [detail?.id]);

  useEffect(() => {
    // 选中商圈或月份范围变化时重新拉取对比数据
    methods.fetchCompareData();
  }, [selectedIds, range]);

  // 选中多个商圈且有对比数据时进入对比模式，日期轴与数据源切换为 compareData
  const isCompareMode = selectedIds.length > 1 && !!compareData;
  const chartDateList = isCompareMode ? compareData?.dateList || [] : dailyFlowChartData?.dateList || [];

  // 单商圈和对比模式统一使用 chartDateList 生成「X月X日」日期轴，筛选范围变化时强制重新计算
  const dailyXAxisData = useMemo(() => {
    const dateList = Array.isArray(chartDateList) ? chartDateList : [];
    return dateList.map((date: string) => {
      const currentDate = dayjs(date);
      return `${currentDate.month() + 1}月${currentDate.date()}日`;
    });
  }, [chartDateList, range]);

  // 建立「x轴文案 → 节假日名」映射：标记区域与 tooltip 共用，用于高亮休息日
  const labelHolidayMap = useMemo(() => {
    const dateList: string[] = chartDateList || [];
    const holidayMap: Record<string, string> = {};
    dateList.forEach((date: string, index: number) => {
      const label = dailyXAxisData[index];
      if (label && holidayDateMap[date]) {
        holidayMap[label] = holidayDateMap[date];
      }
    });
    return holidayMap;
  }, [chartDateList, dailyXAxisData, holidayDateMap, range]);

  // 节假日标记区域：将连续休息日合并为一个区块(区间)，节假日与区间之间允许由非休息日断开；末尾未闭合区间走补丁分支
  const markAreaData = useMemo(() => {
    const markArea: Array<[Record<string, any>, Record<string, any>]> = [];
    let start: string | null = null;
    let currentName: string | null = null;
    let previousLabel: string | null = null;
    dailyXAxisData.forEach((label: string) => {
      const name = labelHolidayMap[label];
      if (name) {
        if (!start) {
          start = label;
          currentName = name;
        }
        previousLabel = label;
      } else if (start && previousLabel) {
        markArea.push([
          { xAxis: start, itemStyle: { color: 'rgba(255, 193, 54, 0.12)' }, label: { show: true, position: 'insideTop', formatter: currentName, color: '#ffffff', fontSize: 12, backgroundColor: '#FFC136', padding: [2, 6], borderRadius: 9, lineHeight: 18 } },
          { xAxis: previousLabel },
        ]);
        start = null;
        currentName = null;
        previousLabel = null;
      }
    });
    // 数据末尾仍处于休息日区间时，补一个未闭合的标记区块
    if (start && previousLabel) {
      markArea.push([
        { xAxis: start, itemStyle: { color: 'rgba(255, 166, 0, 0.1)' }, label: { show: true, position: 'insideTop', formatter: currentName, color: '#FF7A45', fontSize: 12 } },
        { xAxis: previousLabel },
      ]);
    }
    return markArea;
  }, [dailyXAxisData, labelHolidayMap]);

  const chartXAxisData = dailyXAxisData;

  // 每日客流折线系列：对比模式下每商圈一条线(仅第一条附带节假日标记区)；单商圈模式为一条带渐变面积、日均参考线与节假日标记区的折线
  const dailyFlowChartSeriesData = isCompareMode
    ? (Array.isArray(compareData?.mallList) ? compareData.mallList : []).map((item, index) => ({
      name: item?.centerName || `商圈${index + 1}`,
      type: 'line',
      smooth: true,
      showSymbol: true,
      symbolSize: 5,
      data: chartDateList.map((_, dateIndex) => item?.dailyFlowList?.[dateIndex] ?? 0),
      markArea: index === 0 && markAreaData.length ? { silent: false, data: markAreaData } : undefined,
    }))
    : (() => {
      const dailyFlowList = dailyFlowChartData?.dailyFlowList || [];
      const monthlyAvgFlow = dailyFlowChartData?.monthlyAvgFlow;
      return [{
        name: '日客流',
        type: 'line',
        smooth: true,
        showSymbol: true,
        symbolSize: 5,
        data: dailyFlowList,
        areaStyle: {
          // 渐变面积：自上而下由蓝色渐隐，增强面积趋势可读性
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
              { offset: 1, color: 'rgba(64, 158, 255, 0)' },
            ],
          },
        },
        markLine: isNotEmpty(monthlyAvgFlow) ? {
          // 月均客流参考虚线：直观对比每日客流相对月均线的波动
          symbol: ['none', 'arrow'],
          animation: false,
          lineStyle: { type: 'dashed', color: '#FF7A45', width: 1.5 },
          label: { position: 'insideEndTop', formatter: '日均', color: '#ffffff', fontSize: 12, backgroundColor: '#FFC136', lineHeight: 18, padding: [2, 6], borderRadius: 9 },
          data: [{ yAxis: monthlyAvgFlow, name: '日均' }],
        } : undefined,
        markArea: markAreaData.length ? { silent: false, data: markAreaData } : undefined,
      }];
    })();

  // 图表标题下的日期区间文案，取首尾日期
  const chartDateRange = chartDateList.length ? `${dayjs(chartDateList[0]).format('YYYY.MM.DD')} — ${dayjs(chartDateList[chartDateList.length - 1]).format('YYYY.MM.DD')}` : '';
  // 图表标题：按首个日期所在月份生成「X月份每日客流趋势」；数据为空时回退为通用标题
  const chartMonthTitle = chartDateList.length ? `${dayjs(chartDateList[0]).month() + 1}月份每日客流趋势` : '每日客流趋势';
  if (!chartDateList.length) {
    return null;
  }

  return (
    <div className={styles.dailyChartOuter}>
      <div className={styles.dailyChartTop}>
        <div className={styles.dailyChartTitleWrap}>
          <div className={styles.dailyChartTitle}>{chartMonthTitle}</div>
          <div className={styles.dailyChartDateRange}>{chartDateRange}</div>
        </div>
        <Popover
          content={methods.renderCompareContent()}
          trigger='click'
          open={popoverOpen}
          placement='bottomRight'
          overlayClassName={styles.popoverWrap}
          getPopupContainer={(node) => node.parentNode as HTMLElement}
          onOpenChange={methods.onPopoverChange}
        >
          <div className={cs(styles.addCompare, isCompareMode && styles.compareTriggerActive)}>
            <PlusCircleFilled />
            对比({selectedIds.length}/3)
          </div>
        </Popover>
      </div>
      <V2LineChart
        xAxisData={chartXAxisData}
        seriesData={dailyFlowChartSeriesData}
        wrapperClassName={styles.dailyChartWrapper}
        height={200}
        config={{
          animation: false,
          legend: { show: false },
          grid: { top: 30 },
          tooltip: {
            trigger: 'axis',
            formatter: methods.dailyTooltipFormatter,
          },
        }}
      />
      {isCompareMode && (
        <div className={styles.compareLegend}>
          {dailyFlowChartSeriesData.map((item: any, index: number) => (
            <div
              className={styles.compareLegendItem}
              key={item.name}
            >
              <div
                className={styles.compareLegendColor}
                style={{ backgroundColor: ['#358AFF', '#39D3F9', '#FFA140'][index % 3] }}
              />
              <span>{item.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DailyFlowTrend;
